import React from "react";
import PropTypes from "prop-types";
import Radio from "@mui/material/Radio";
import Grid from "@mui/material/Grid";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@limitless/modern/inputs/button/Button";
import MultilineTextField from "@limitless/modern/inputs/multiline-text-field/MultilineTextField";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Link from "@mui/material/Link";
import makeStyles from "@mui/styles/makeStyles";
import { useExpertSupportMessageCreationContext } from "@limitless/expert";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import Lock from "@limitless/icons/Lock";
import { useExpertProfileContext } from "@limitless/expert";

const useStyles = makeStyles(theme => ({
    getInTouchLayout: {
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px"
    },
    body: {
        marginTop: "12px",
        display: "inline"
    },
    faqSection: {
        display: "inline",
        cursor: "pointer",
        color: theme.palette.primary.main,
        textDecoration: "none",
        lineHeight: "18px",
        marginRight: "4px",
        marginLeft: "4px"
    },
    display: {
        display: "inline"
    },
    caption: {
        display: "block",
        marginTop: "12px"
    },
    body2: {
        marginTop: "16px",
        size: "14px"
    }
}));

const GetInTouchScreen = ({ supportedGroups, privateSupport, messageId }) => {
    const classes = useStyles();
    const {
        profile: { expertTrustedLocationType }
    } = useExpertProfileContext();
    const isSecureAccess = ["platform", "tenant"].includes(expertTrustedLocationType);

    const {
        handleQueryDetailsChange,
        setSelectedTabIndex,
        setShowLLMLanding,
        handleSubmitMessage,
        queryDescription,
        setQueryTopic,
        queryTopic,
        error,
        setGetinTouchScreen,
        setSubmitMessage,
        setIsReceived,
        setQueryDescription
    } = useExpertSupportMessageCreationContext();

    React.useEffect(() => {
        if (messageId) {
            if (process.env.REACT_APP_STAGE === "testc" || process.env.REACT_APP_STAGE === "dev300") {
                setQueryTopic("Other");
            } else {
                setQueryTopic("Other-ExpertSupport");
            }
            setQueryDescription(`Quality Review Message ID: ${messageId}`);
        }
    }, [messageId]);

    const privateSupportGroupName = privateSupport.groupName; // make sure to have groupName in config 'expertSupportDetails'

    const categoryTopic = supportedGroups?.filter(item => item.value !== privateSupportGroupName) || [];

    const redirectToContactUs = () => {
        setSelectedTabIndex(0);
        setShowLLMLanding(false);
        setGetinTouchScreen(false);
        setSubmitMessage(false);
        setIsReceived(false);
    };

    return (
        <>
            <div className={classes.getInTouchLayout}>
                <Typography variant="h6">{"Get in touch"}</Typography>

                <Typography variant="caption" weight="400" component="div" className={classes.body}>
                    {"Need help with the Limitless app? Look at the"}
                    <Typography
                        variant="caption"
                        weight="400"
                        className={classes.faqSection}
                        component={Link}
                        onClick={redirectToContactUs}
                    >
                        {"FAQ section"}
                    </Typography>
                    <Typography variant="caption" weight="400" className={classes.display}>
                        {
                            "to see if your query is answered there. If you still need help, complete the form below and our Expert Support team will get back to you as soon as possible. If you would like to share an attachment, you will be given the option once you submit your query."
                        }
                    </Typography>
                    <div>
                        <Typography variant="caption" weight="400" className={classes.caption}>
                            {"Please only submit one message."}
                        </Typography>
                    </div>
                </Typography>

                <div style={{ display: "flex", alignItems: "end", gap: "6px" }}>
                    <Typography variant="body2" weight="400" className={classes.body2} component="div">
                        {"What is your query about?"}
                    </Typography>
                    {isSecureAccess && (
                        <IconButton
                            icon={<Lock />}
                            label="Showing additional options based on your Secure location"
                            id="contactUsLockIcon"
                            sx={{
                                width: 23,
                                height: 23
                            }}
                        />
                    )}
                </div>
                <Grid justifyContent={"center"} alignItems={"flex-end"} gap={"8px"}>
                    <RadioGroup value={queryTopic} onChange={event => setQueryTopic(event.target.value)}>
                        {categoryTopic.map((item, index) => {
                            return (
                                <FormControlLabel
                                    key={index}
                                    value={item.value}
                                    control={<Radio style={{ color: "#3372FF" }} />}
                                    label={<Typography>{item.label}</Typography>}
                                />
                            );
                        })}
                    </RadioGroup>
                </Grid>
                <Grid container spacing={2} style={{ marginBottom: "25px" }}>
                    <Grid item xs={12}>
                        <MultilineTextField
                            label="Please provide more details below"
                            name="Please provide more details below"
                            value={queryDescription}
                            onChange={handleQueryDetailsChange}
                            required
                            style={{ width: "435px", height: "109px" }}
                            placeholder="Type here..."
                            fullWidth
                            multiline
                            errorText={"Must be at least 10 characters"}
                            error={error}
                            helperText={error ? "Must be at least 10 characters" : ""}
                            rows={5}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={queryTopic === "" || queryDescription === "" || error}
                            onClick={handleSubmitMessage}
                        >
                            SUBMIT
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

export default GetInTouchScreen;

GetInTouchScreen.propTypes = {
    supportedGroups: PropTypes.array,
    privateSupport: PropTypes.object,
    messageId: PropTypes.string
};
